<template>
  <section class="container mx-auto px-4 md:px-8 py-10 md:py-14">
    <h1
      class="text-3xl md:text-4xl font-extrabold mb-8 text-center md:text-left"
    >
      Контакти
    </h1>

    <div class="grid grid-cols-1 lg:grid-cols-2 gap-10">
      <!-- Лівий стовпець -->
      <div class="space-y-6">
        <div class="border rounded-2xl p-5">
          <h2 class="text-xl font-semibold mb-4">Звʼязатися з нами</h2>
          <ul class="space-y-3 text-gray-700">
            <li class="flex items-center gap-3">
              <Phone class="shrink-0" :size="18" />
              <a class="hover:text-pink-200 transition" href="tel:+380966668451"
                >+380 (96) 666 84 51</a
              >
            </li>
            <li class="flex items-center gap-3">
              <Mail class="shrink-0" :size="18" />
              <a
                class="hover:text-pink-200 transition"
                href="mailto:mulberryss.silkstudio@gmail.com"
                >mulberryss.silkstudio@gmail.com</a
              >
            </li>
            <li class="flex items-center gap-3">
              <Clock class="shrink-0" :size="18" /><span
                >Пн–Нд: 10:00–21:00</span
              >
            </li>
            <li class="flex items-center gap-3">
              <MapPin class="shrink-0" :size="18" /><span
                >Київ, Україна (онлайн-магазин)</span
              >
            </li>
          </ul>

          <div class="mt-5 flex items-center gap-3">
            <a
              href="https://www.instagram.com/mulberry_silk_studio"
              target="_blank"
              rel="noopener"
              class="group inline-flex items-center gap-2"
              aria-label="Instagram"
            >
              <Instagram
                :size="22"
                class="transition-colors group-hover:text-pink-200"
              />
              <span class="group-hover:text-pink-200 transition"
                >Підписатись в Instagram</span
              >
            </a>
          </div>
        </div>

        <div class="border rounded-2xl p-5">
          <h2 class="text-xl font-semibold mb-4">Доставка та оплата</h2>
          <div class="flex items-center gap-4">
            <img
              src="/img/np-logo.png"
              alt="Нова Пошта"
              class="h-8 w-auto"
              loading="lazy"
            />
            <div class="text-sm text-gray-700">
              Доставка по Україні <b>2–5 днів</b> службою
              <b>Нова Пошта</b>.<br />
              Оплата онлайн або накладений платіж.
            </div>
          </div>
          <NuxtLink
            to="/delivery"
            class="inline-flex items-center gap-2 mt-4 px-4 py-2 rounded-xl border hover:bg-gray-50 transition text-sm"
          >
            <Truck :size="18" /> Детальніше про доставку
          </NuxtLink>
        </div>

        <div class="border rounded-2xl p-5">
          <h2 class="text-xl font-semibold mb-2">Швидкий контакт</h2>
          <p class="text-sm text-gray-700 mb-3">
            Напишіть нам у месенджері — відповімо якнайшвидше.
          </p>
          <div class="flex flex-wrap gap-3">
            <a
              href="https://t.me/mulberry_ss"
              target="_blank"
              class="px-4 py-2 rounded-xl bg-pink-200 text-black hover:bg-pink-200/80 transition text-sm"
              >Telegram</a
            >
            <a
              href="https://ig.me/m/mulberry_silk_studio"
              target="_blank"
              class="px-4 py-2 rounded-xl bg-pink-200 text-black hover:bg-pink-200/80 text-sm"
              >Instagram</a
            >
          </div>
        </div>
      </div>

      <!-- Правий стовпець: форма -->
      <div class="border rounded-2xl p-5">
        <h2 class="text-xl font-semibold mb-4">Форма звернення</h2>

        <form novalidate class="space-y-4" @submit.prevent="onSubmit">
          <div>
            <label class="block text-sm mb-1">Імʼя*</label>
            <input
              v-model.trim="form.name"
              type="text"
              required
              class="w-full border rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-pink-200"
              placeholder="Ваше імʼя"
            />
          </div>

          <div>
            <label class="block text-sm mb-1">Телефон*</label>

            <input
              v-model="form.phone"
              v-phone-ua
              type="tel"
              required
              autocomplete="tel"
              inputmode="tel"
              :placeholder="PHONE_PLACEHOLDER"
              class="w-full border rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-pink-200"
              :aria-invalid="phoneTouched && !isPhoneValid ? 'true' : 'false'"
              @input="phoneTouched = true"
              @blur="phoneTouched = true"
            />

            <p
              v-if="phoneTouched && form.phone && !isPhoneValid"
              class="text-xs text-red-600 mt-1"
            >
              {{ phoneErrorText }}
            </p>
          </div>

          <div>
            <label class="block text-sm mb-1">Повідомлення*</label>
            <textarea
              v-model.trim="form.message"
              required
              rows="5"
              class="w-full border rounded-lg px-3 py-2 outline-none resize-y focus:ring-2 focus:ring-pink-200"
              placeholder="Чим можемо допомогти?"
            />
          </div>

          <div class="text-xs text-gray-500">
            Натискаючи “Надіслати”, ви погоджуєтесь з нашою
            <NuxtLink to="/privacy" class="underline hover:text-pink-200"
              >політикою конфіденційності</NuxtLink
            >.
          </div>

          <div class="flex flex-wrap items-center gap-3 pt-2">
            <button
              type="submit"
              class="px-6 py-3 rounded-xl font-semibold transition bg-pink-200 text-black hover:bg-pink-200/80 disabled:opacity-50"
              :disabled="submitting || !allValid"
            >
              {{ submitting ? "Надсилаємо…" : "Надіслати" }}
            </button>

            <span v-if="sent" class="text-green-600 text-sm"
              >Дякуємо! Ми отримали ваше повідомлення.</span
            >
            <span v-else-if="error" class="text-red-600 text-sm"
              >Сталася помилка. Спробуйте ще раз.</span
            >
          </div>
        </form>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import { Phone, Mail, Clock, MapPin, Instagram, Truck } from "lucide-vue-next";
import { useContactForm } from "~/composables/useContactForm";
import {
  toE164,
  isValidUaPhone,
  isValidUaOperator,
  uaLocalDigits,
  PHONE_PLACEHOLDER,
  PHONE_FORMAT_HINT,
  PHONE_OPERATOR_HINT,
} from "~/utils/phone";

useHead({ title: "Контакти — mulberry’ss" });

const form = ref({ name: "", phone: "", message: "" });

/** Валідація та E.164 */
const isPhoneValid = computed(() => isValidUaPhone(form.value.phone));
const phoneE164 = computed(() =>
  isPhoneValid.value ? toE164(form.value.phone) : "",
);
const phoneErrorText = computed(() =>
  uaLocalDigits(form.value.phone).length === 9 &&
  !isValidUaOperator(form.value.phone)
    ? PHONE_OPERATOR_HINT
    : PHONE_FORMAT_HINT,
);

const phoneTouched = ref(false);
// SSR-safe: in production the page is server-rendered on first hit.
// `useContactForm` (and its deps) must be initialized only on client.
let send = async () => null;
const submitting = ref(false);
const error = ref(null);

if (import.meta.client) {
  const api = useContactForm();
  send = api.send;
  watch(
    api.loading,
    (v) => {
      submitting.value = !!v;
    },
    { immediate: true },
  );
  watch(
    api.error,
    (v) => {
      error.value = v;
    },
    { immediate: true },
  );
}

const sent = ref(false);

const allValid = computed(() => {
  const nameOk = !!form.value.name?.trim();
  const msgOk = !!form.value.message?.trim();
  return nameOk && msgOk && isPhoneValid.value;
});

async function onSubmit() {
  if (!allValid.value) {
    phoneTouched.value = true;
    return;
  }
  sent.value = false;

  const payload = { ...form.value, phoneE164: phoneE164.value };
  const id = await send(payload);
  if (id) {
    sent.value = true;
    // form.value = { name: "", phone: "", message: "" };
  }
}
</script>
